import { useEffect, useMemo, useState } from 'react'
import type { BadgeOverviewItem } from '@/features/poules/api'
import { usePouleUserBadges } from '@/features/poules/api'
import {
  type BadgeCategory,
  getBadgeDisplayImage,
  getBadgeProgress,
  normalizeString,
  splitBadgesByCategory,
} from '@/features/poules/badges'
import { useCurrentUser } from '@/features/user/api'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { getLocale, toIntlLocale } from '@/config/locale'
import { translate, useTranslation } from '@/i18n'
import {
  getTeamLabel,
  toUiRankingUsers,
  type TeamRankingItem,
  type UiRankingUser,
  usePouleStandings,
  usePouleStandingsSearch,
  usePouleTeamRanking,
} from '@/features/poules/ranking'

export function PouleRankingTab({
  pouleId,
  supportsTeams,
}: {
  pouleId: number
  supportsTeams: boolean
}) {
  const { t } = useTranslation()
  const [showTeams, setShowTeams] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<UiRankingUser | null>(null)
  const currentUserQuery = useCurrentUser(true)
  const standingsQuery = usePouleStandings(pouleId, !showTeams)
  const standingsSearchQuery = usePouleStandingsSearch(
    pouleId,
    debouncedSearchQuery,
    !showTeams,
  )
  const teamRankingQuery = usePouleTeamRanking(pouleId, showTeams && supportsTeams)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim())
    }, 300)

    return () => window.clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    if (!supportsTeams && showTeams) {
      setShowTeams(false)
    }
  }, [showTeams, supportsTeams])

  useEffect(() => {
    if (showTeams) {
      setSelectedUser(null)
    }
  }, [showTeams])

  const baseRankingUsers = useMemo(
    () => toUiRankingUsers(standingsQuery.data?.users ?? []),
    [standingsQuery.data?.users],
  )
  const hasSearchResponse = standingsSearchQuery.data != null
  const rankingUsers = useMemo(
    () =>
      debouncedSearchQuery.length > 0 && hasSearchResponse
        ? toUiRankingUsers(standingsSearchQuery.data?.users ?? [])
        : baseRankingUsers,
    [baseRankingUsers, debouncedSearchQuery, hasSearchResponse, standingsSearchQuery.data?.users],
  )
  const rankingIsLoading =
    standingsQuery.isLoading ||
    (debouncedSearchQuery.length > 0 &&
      !hasSearchResponse &&
      standingsSearchQuery.isLoading)
  const rankingIsError =
    standingsQuery.isError ||
    (debouncedSearchQuery.length > 0 &&
      hasSearchResponse &&
      standingsSearchQuery.isError)
  const rankingIsFetching =
    standingsQuery.isFetching || standingsSearchQuery.isFetching
  const currentUserStanding = useMemo(() => {
    const currentUserId = currentUserQuery.data?.id
    if (!currentUserId) return undefined

    return standingsQuery.data?.users.find((user) => user.user_id === currentUserId)
  }, [currentUserQuery.data?.id, standingsQuery.data?.users])

  return (
    <div className="space-y-5">
      <section className="rounded-[1.35rem] border border-[#202427] bg-[#0a0d0f] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {supportsTeams ? (
            <div className="grid grid-cols-2 gap-1.5 rounded-[0.95rem] border border-[#202427] bg-[#0d1011] p-1">
              <ToggleButton
                active={!showTeams}
                onClick={() => setShowTeams(false)}
              >
                {t('Individuals')}
              </ToggleButton>
              <ToggleButton
                active={showTeams}
                onClick={() => setShowTeams(true)}
              >
                {t('Teams')}
              </ToggleButton>
            </div>
          ) : null}
        </div>

        {!showTeams ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="w-full max-w-md">
              <label htmlFor="poule-ranking-search" className="sr-only">
                {t('Zoek in ranking')}
              </label>
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-surface-500"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2">
                    <circle cx="11" cy="11" r="6.5" />
                    <path d="m16 16 4 4" strokeLinecap="round" />
                  </svg>
                </span>
                <input
                  id="poule-ranking-search"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('Zoek op naam')}
                  className="h-11 w-full rounded-full border border-white/[0.08] bg-[#111416] pl-10 pr-10 text-sm text-white placeholder:text-surface-500 transition hover:border-white/[0.14] focus:border-accent-500 focus:bg-[#151a1c] focus:outline-none focus:ring-1 focus:ring-accent-500/40"
                />
                {searchQuery ? (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    aria-label={t('Wis zoekopdracht')}
                    className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-sm text-surface-500 transition hover:bg-white/[0.08] hover:text-white focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111416]"
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>

            {currentUserStanding ? (
              <CurrentUserChip
                rank={currentUserStanding.rank}
                name={currentUserStanding.name}
                points={currentUserStanding.total_points}
              />
            ) : null}
          </div>
        ) : currentUserStanding ? (
          <div>
            <CurrentUserChip
              rank={currentUserStanding.rank}
              name={currentUserStanding.name}
              points={currentUserStanding.total_points}
            />
          </div>
        ) : null}
      </section>

      {!showTeams ? (
        <IndividualRankingState
          items={rankingUsers}
          isLoading={rankingIsLoading}
          isError={rankingIsError}
          isFetching={rankingIsFetching}
          query={debouncedSearchQuery}
          onRetry={() =>
            void (debouncedSearchQuery
              ? standingsSearchQuery.refetch()
              : standingsQuery.refetch())
          }
          onSelectUser={setSelectedUser}
        />
      ) : (
        <TeamRankingState
          items={teamRankingQuery.data?.teams ?? []}
          isLoading={teamRankingQuery.isLoading}
          isError={teamRankingQuery.isError}
          onRetry={() => void teamRankingQuery.refetch()}
        />
      )}

      {selectedUser ? (
        <RankingUserDetailSheet
          pouleId={pouleId}
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      ) : null}
    </div>
  )
}

function IndividualRankingState({
  items,
  isLoading,
  isError,
  isFetching,
  query,
  onRetry,
  onSelectUser,
}: {
  items: UiRankingUser[]
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  query: string
  onRetry: () => void
  onSelectUser: (user: UiRankingUser) => void
}) {
  const { t } = useTranslation()
  if (isLoading && items.length === 0) {
    return <InfoPanel title={t('Ranking laden')} body={t('We halen de huidige stand van deze poule op.')} />
  }

  if (isError && items.length === 0) {
    return (
      <ErrorPanel
        title={t('Ranking niet geladen')}
        body={t('De individuele ranking kon niet worden geladen.')}
        onRetry={onRetry}
      />
    )
  }

  if (items.length === 0) {
    return (
      <InfoPanel
        title={query ? t('Geen zoekresultaten') : t('Nog geen ranking')}
        body={
          query
            ? t('Er zijn geen deelnemers gevonden voor deze zoekopdracht.')
            : t('Er is nog geen individuele ranking beschikbaar voor deze poule.')
        }
      />
    )
  }

  return (
    <section className="rounded-[1.35rem] border border-[#202427] bg-[#0a0d0f] p-3 sm:p-4">
      <div className="flex h-6 items-center justify-end px-2">
        {isFetching ? (
          <div className="app-progress-track h-1 w-full overflow-hidden rounded-full bg-[#151a1c]">
            <div className="animate-ranking-loader h-full w-1/3 rounded-full bg-accent-500" />
          </div>
        ) : null}
      </div>

      <div className="space-y-2.5">
        {items.map((item) => (
          <IndividualRow key={item.userId} item={item} onClick={() => onSelectUser(item)} />
        ))}
      </div>
    </section>
  )
}

function TeamRankingState({
  items,
  isLoading,
  isError,
  onRetry,
}: {
  items: TeamRankingItem[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()
  if (isLoading) {
    return <InfoPanel title={t('Team ranking laden')} body={t('We halen de teamstand van deze poule op.')} />
  }

  if (isError) {
    return (
      <ErrorPanel
        title={t('Team ranking niet geladen')}
        body={t('De team ranking kon niet worden geladen.')}
        onRetry={onRetry}
      />
    )
  }

  if (items.length === 0) {
    return (
      <InfoPanel
        title={t('Nog geen team ranking')}
        body={t('Er is nog geen team ranking beschikbaar voor deze poule.')}
      />
    )
  }

  return (
    <section className="rounded-[1.35rem] border border-[#202427] bg-[#0a0d0f] p-3 sm:p-4">
      <div className="space-y-2.5">
        {items.map((item) => (
          <TeamRow key={item.team_id} item={item} />
        ))}
      </div>
    </section>
  )
}

function IndividualRow({
  item,
  onClick,
}: {
  item: UiRankingUser
  onClick: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[1rem] border border-[#202427] bg-[#0d1011] px-3.5 py-3 text-left transition hover:border-[#24405b] hover:bg-[#0e1520] focus-visible:border-accent-400 focus-visible:bg-[#0e1520] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0d0f]"
    >
      <RankBadge rank={item.rank} />
      <Avatar name={item.name} profileImage={item.profileImage} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold text-white">{item.name}</p>
          <MovementPill movement={item.rankMovement} />
        </div>
        <p className="mt-1 truncate text-xs text-surface-400">
          {item.teamName || t('Geen team gekoppeld')}
        </p>
      </div>
      <div className="text-right">
        <p className="text-base font-black text-white">{item.totalPoints}</p>
        <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-surface-500">{t('punten')}</p>
      </div>
    </button>
  )
}

function TeamRow({ item }: { item: TeamRankingItem }) {
  const { t } = useTranslation()
  return (
    <article className="flex items-center gap-3 rounded-[1rem] border border-[#202427] bg-[#0d1011] px-3.5 py-3">
      <RankBadge rank={item.rank} />
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#151a1c] text-sm font-black uppercase text-surface-300">
        {getInitials(getTeamLabel(item))}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">{getTeamLabel(item)}</p>
        <p className="mt-1 text-xs text-surface-400">
          {item.member_count} {item.member_count === 1 ? t('lid') : t('leden')}
        </p>
      </div>
      <div className="text-right">
        <p className="text-base font-black text-white">
          {typeof item.total_points === 'number' ? item.total_points : 0}
        </p>
        <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-surface-500">{t('punten')}</p>
      </div>
    </article>
  )
}

function RankingUserDetailSheet({
  pouleId,
  user,
  onClose,
}: {
  pouleId: number
  user: UiRankingUser
  onClose: () => void
}) {
  const { t } = useTranslation()
  const badgesQuery = usePouleUserBadges(pouleId, user.userId, { enabled: true })
  const badges = badgesQuery.data?.data ?? []
  const [activeCategory, setActiveCategory] = useState<BadgeCategory>('regular')

  const groupedBadges = useMemo(() => splitBadgesByCategory(badges), [badges])
  const regularBadges = groupedBadges.regular
  const clubBadges = groupedBadges.club
  const visibleBadges = activeCategory === 'club' ? clubBadges : regularBadges
  const activeProgress = getBadgeProgress(visibleBadges)
  const totalProgress = getBadgeProgress(badges)

  useEffect(() => {
    if (activeCategory === 'regular' && regularBadges.length === 0 && clubBadges.length > 0) {
      setActiveCategory('club')
      return
    }

    if (activeCategory === 'club' && clubBadges.length === 0 && regularBadges.length > 0) {
      setActiveCategory('regular')
    }
  }, [activeCategory, clubBadges.length, regularBadges.length])

  return (
    <div className="animate-drawer-backdrop-in fixed inset-0 z-[72] bg-black/88 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[84vh] w-full items-stretch px-3 pb-3 sm:px-4">
        <div className="animate-manager-player-picker-in relative mx-auto flex w-full max-w-4xl flex-col overflow-hidden rounded-[1.4rem] border border-[#202427] bg-[#05080d] shadow-[0_18px_56px_rgba(0,0,0,0.5)]">
          <div className="border-b border-[#142030] px-4 pb-4 pt-3 sm:px-5">
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-surface-700" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                  {t('Speler details')}
                </p>
                <h2 className="mt-2 text-xl font-black text-white">{user.name}</h2>
                <p className="mt-1.5 text-sm text-surface-400">
                  {t('Bekijk behaalde en nog vergrendelde trofeeën binnen deze poule.')}
                </p>
              </div>
              <ModalCloseButton onClick={onClose} />
            </div>
          </div>

          <div className="overflow-y-auto p-4 sm:p-5">
            <section className="rounded-[1.2rem] border border-[#202427] bg-[#0d1011] p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <Avatar name={user.name} profileImage={user.profileImage} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-xl font-black text-white">{user.name}</p>
                    <span className="rounded-full border border-[#203841] bg-[#10191c] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#93d9ff]">
                      {typeof user.rank === 'number' ? `#${user.rank}` : t('Geen rank')}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-surface-400">
                    <span>{user.totalPoints} {t('punten')}</span>
                    {user.teamName ? (
                      <>
                        <span aria-hidden="true" className="h-1 w-1 rounded-full bg-surface-700" />
                        <span>{user.teamName}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <MetricCard label={t('Totaal verdiend')} value={`${totalProgress.earned}/${totalProgress.total}`} />
                <MetricCard
                  label={t('Reguliere trofeeën')}
                  value={`${getBadgeProgress(regularBadges).earned}/${regularBadges.length}`}
                />
                <MetricCard
                  label={t('Club trofeeën')}
                  value={`${getBadgeProgress(clubBadges).earned}/${clubBadges.length}`}
                />
              </div>
            </section>

            <section className="mt-5 rounded-[1.2rem] border border-[#202427] bg-[#0d1011] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                    {t('Trofeeën')}
                  </p>
                  <h3 className="mt-2 text-lg font-black text-white">
                    {activeCategory === 'club' ? t('Club trofeeën') : t('Reguliere trofeeën')}
                  </h3>
                  <p className="mt-1 text-sm text-surface-400">
                    {t('Voortgang {earned}/{total}', { earned: activeProgress.earned, total: activeProgress.total })}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-1.5 rounded-[0.95rem] border border-[#202427] bg-[#0d1113] p-1">
                  <CategoryTab
                    label={t('Reguliere trofeeën')}
                    count={regularBadges.length}
                    active={activeCategory === 'regular'}
                    disabled={regularBadges.length === 0}
                    onClick={() => setActiveCategory('regular')}
                  />
                  <CategoryTab
                    label={t('Club trofeeën')}
                    count={clubBadges.length}
                    active={activeCategory === 'club'}
                    disabled={clubBadges.length === 0}
                    onClick={() => setActiveCategory('club')}
                  />
                </div>
              </div>

              {badgesQuery.isLoading && badges.length === 0 ? (
                <InfoPanel
                  title={t('Badges laden')}
                  body={t('We halen de badgecatalogus van deze gebruiker op.')}
                />
              ) : null}

              {badgesQuery.isError && badges.length === 0 ? (
                <ErrorPanel
                  title={t('Badges niet geladen')}
                  body={badgesQuery.error?.message || t('De badges konden niet worden geladen.')}
                  onRetry={() => void badgesQuery.refetch()}
                />
              ) : null}

              {!badgesQuery.isLoading && !badgesQuery.isError && badges.length === 0 ? (
                <InfoPanel
                  title={t('Nog geen badges beschikbaar')}
                  body={t('Voor deze gebruiker is nog geen badgecatalogus beschikbaar in deze poule.')}
                />
              ) : null}

              {badges.length > 0 ? (
                <>
                  {badgesQuery.isFetching ? (
                    <p className="mt-4 text-right text-xs uppercase tracking-[0.18em] text-surface-600">
                      Badges worden ververst…
                    </p>
                  ) : null}

                  {visibleBadges.length === 0 ? (
                    <InfoPanel
                      title={
                        activeCategory === 'club'
                          ? t('Nog geen club trofeeën')
                          : t('Nog geen reguliere trofeeën')
                      }
                      body={t('Wissel van categorie of kom later terug zodra er badges beschikbaar zijn.')}
                    />
                  ) : (
                    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {visibleBadges.map((badge) => (
                        <BadgeTile key={badge.id} badge={badge} category={activeCategory} />
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

function CategoryTab({
  label,
  count,
  active,
  disabled,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'rounded-[0.8rem] px-3 py-2 text-sm font-semibold transition',
        active
          ? 'bg-[#3f7890] text-white shadow-[0_8px_18px_rgba(17,137,199,0.22)]'
          : 'text-surface-400 hover:bg-[#151a1c] hover:text-surface-100',
        'disabled:cursor-default disabled:opacity-45',
      ].join(' ')}
    >
      <span className="block truncate">{label}</span>
      <span className="mt-0.5 block text-[11px] uppercase tracking-[0.12em] opacity-75">
        {count}
      </span>
    </button>
  )
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[1rem] bg-[#111517] px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
        {label}
      </p>
      <p className="mt-1.5 text-lg font-black text-white">{value}</p>
    </div>
  )
}

function BadgeTile({
  badge,
  category,
}: {
  badge: BadgeOverviewItem
  category: BadgeCategory
}) {
  const { t } = useTranslation()
  const imageUrl = getBadgeDisplayImage(badge)
  const tierLabel = normalizeString(badge.tier?.label)
  const contextLabel =
    normalizeString(badge.club?.name) ??
    badge.earned_instances
      .map((instance) => normalizeString(instance.club?.name))
      .find(Boolean)
  const subtitle = badge.earned
    ? badge.earned_count > 1
      ? `${badge.earned_count}x verdiend`
      : badge.last_earned_at
        ? `Laatste keer ${formatShortDate(badge.last_earned_at)}`
        : t('Verdiend')
    : t('Vergrendeld')

  return (
    <article
      className={[
        'rounded-[1rem] border p-4 transition',
        badge.earned
          ? 'border-[#202427] bg-[#0c131d]'
          : 'border-[#151d2a] bg-[#090f16] opacity-65',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <BadgeTileArtwork imageUrl={imageUrl} name={badge.badge.name} locked={!badge.earned} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-black text-white">{badge.badge.name}</p>
            <span
              className={[
                'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]',
                badge.earned
                  ? 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-200'
                  : 'border border-surface-700 bg-surface-900 text-surface-400',
              ].join(' ')}
            >
              {badge.earned ? t('Verdiend') : t('Vergrendeld')}
            </span>
          </div>
          {tierLabel ? (
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#93d9ff]">
              {tierLabel}
            </p>
          ) : null}
          <p className="mt-2 text-xs text-surface-400">{subtitle}</p>
          {category === 'club' && contextLabel ? (
            <p className="mt-2 text-xs text-surface-500">{contextLabel}</p>
          ) : null}
        </div>
      </div>

      <p className="mt-3 line-clamp-2 text-sm text-surface-300">
        {normalizeString(badge.badge.description) || t('Geen beschrijving beschikbaar.')}
      </p>
    </article>
  )
}

function BadgeTileArtwork({
  imageUrl,
  name,
  locked,
}: {
  imageUrl?: string | undefined
  name: string
  locked: boolean
}) {
  return (
    <div
      className={[
        'flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[0.95rem] border bg-[#101824]',
        locked ? 'border-[#202427]' : 'border-[#35444a]',
      ].join(' ')}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className={`h-full w-full object-cover ${locked ? 'grayscale-[0.85]' : ''}`}
        />
      ) : (
        <span className="text-lg text-surface-300">{getInitials(name)}</span>
      )}
    </div>
  )
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-[0.8rem] px-3.5 py-2 text-sm font-semibold transition',
        active
          ? 'bg-[#3f7890] text-white shadow-[0_8px_18px_rgba(17,137,199,0.22)]'
          : 'text-surface-400 hover:bg-[#151a1c] hover:text-surface-100',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function CurrentUserChip({
  rank,
  name,
  points,
}: {
  rank?: number | null | undefined
  name: string
  points: number
}) {
  const { t } = useTranslation()
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[#203841] bg-[#10191c] px-3 py-1.5 text-xs font-semibold text-[#93d9ff]">
      <span>{typeof rank === 'number' ? `#${rank}` : t('Geen rank')}</span>
      <span aria-hidden="true" className="h-3.5 w-px bg-[#1a5275]" />
      <span>{name}</span>
      <span aria-hidden="true" className="h-3.5 w-px bg-[#1a5275]" />
      <span>{points} pt</span>
    </div>
  )
}

function RankBadge({ rank }: { rank?: number | null | undefined }) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#151a1c] text-sm font-black text-white">
      {typeof rank === 'number' ? rank : '–'}
    </div>
  )
}

function MovementPill({
  movement,
}: {
  movement?: 'up' | 'down' | 'same' | 'new' | null | undefined
}) {
  const { t } = useTranslation()
  if (!movement || movement === 'same') return null

  const className =
    movement === 'up'
      ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200'
      : movement === 'down'
        ? 'border-red-500/25 bg-red-500/10 text-red-200'
        : 'border-[#203841] bg-[#10191c] text-[#93d9ff]'

  return (
    <span
      aria-label={movement === 'up' ? t('Omhoog') : movement === 'down' ? t('Omlaag') : t('Nieuw')}
      title={movement === 'up' ? t('Omhoog') : movement === 'down' ? t('Omlaag') : t('Nieuw')}
      className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full border px-1.5 text-xs font-black leading-none ${className}`}
    >
      {movement === 'up' ? '↑' : movement === 'down' ? '↓' : t('Nieuw')}
    </span>
  )
}

function Avatar({
  name,
  profileImage,
}: {
  name: string
  profileImage?: string | null | undefined
}) {
  if (profileImage) {
    return (
      <img
        src={profileImage}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full object-cover"
      />
    )
  }

  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#151a1c] text-sm font-black uppercase text-surface-300">
      {getInitials(name)}
    </span>
  )
}

function ErrorPanel({
  title,
  body,
  onRetry,
}: {
  title: string
  body: string
  onRetry: () => void
}) {
  const { t } = useTranslation()
  return (
    <section className="rounded-[1.35rem] border border-red-900/60 bg-red-950/25 p-5 text-red-100">
      <h2 className="text-xl font-black tracking-tight">{title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-red-100/80">{body}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-full border border-red-200/20 px-4 py-2 text-sm font-semibold transition hover:bg-white/5"
      >
        {t('Opnieuw proberen')}
      </button>
    </section>
  )
}

function InfoPanel({
  title,
  body,
}: {
  title: string
  body: string
}) {
  return (
    <section className="rounded-[1.35rem] border border-[#202427] bg-[#0a0d0f] p-5">
      <h2 className="text-xl font-black tracking-tight text-white">{title}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">{body}</p>
    </section>
  )
}

function formatShortDate(value?: string | null) {
  if (!value) return translate('Onbekend')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(toIntlLocale(getLocale()), {
    day: 'numeric',
    month: 'short',
  }).format(date)
}

function getInitials(value: string) {
  return value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}
